// src/utils/gpsAnalysis.js
// Requiere: date-fns + date-fns-tz (ya los tienes)
import { utcToZonedTime } from "date-fns-tz";
import { format } from "date-fns";

/**
 * Config por defecto (ajústala a tu operación):
 * - gapSec: si pasan muchos segundos sin puntos, cortamos el trazo
 * - gapKm: si salta demasiados km entre dos puntos, cortamos
 * - maxKmh: si la velocidad implícita es absurda, cortamos (o marcamos outlier)
 * - stopDistKm: distancia "casi cero" para considerar que está quieto
 * - stopMinSec: mínimo de segundos quieto para declarar una parada
 * - simplify: reduce puntos para mejorar performance (opcional)
 */
export const DEFAULT_GPS_ANALYSIS_CFG = {
  timeZone: "America/Lima",
  gapSec: 180,      // 3 min sin señal -> cortar tramo
  gapKm: 2.0,       // salto > 2 km -> cortar tramo (ajusta según rutas)
  maxKmh: 120,      // velocidad irreal -> cortar / outlier
  stopDistKm: 0.015, // ~15m
  stopMinSec: 60,    // 1 min quieto
  // Optimización: reduce puntos para dibujar
  simplify: {
    enabled: true,
    // si hay más de N puntos, reducimos
    maxPoints: 1500,
    // tolerancia en km para simplificar (Douglas-Peucker)
    toleranceKm: 0.02, // 20m aprox
  },
  // Para pintar por velocidad (si usas segmentación por pares)
  speedBins: [
    { lt: 10, label: "lento" },
    { lt: 40, label: "medio" },
    { lt: Infinity, label: "rápido" },
  ],
};

/* -------------------------- helpers base -------------------------- */

export const haversineKm = (a, b) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
};

const toTZLabel = (utcIso, timeZone) => {
  // etiqueta consistente (no depende del navegador)
  const zoned = utcToZonedTime(utcIso, timeZone);
  return format(zoned, "dd-MM-yyyy HH:mm:ss");
};

const safeNumber = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

const speedKmhBetween = (a, b) => {
  const dtSec = (new Date(b.timestamp) - new Date(a.timestamp)) / 1000;
  if (dtSec <= 0) return { dtSec, dKm: 0, speedKmh: 0 };
  const dKm = haversineKm(a, b);
  const speedKmh = dKm / (dtSec / 3600);
  return { dtSec, dKm, speedKmh };
};

const getSpeedBin = (speedKmh, bins) => {
  for (let i = 0; i < bins.length; i++) {
    if (speedKmh < bins[i].lt) return bins[i].label;
  }
  return bins.at(-1)?.label ?? "unknown";
};

/* --------------------- Simplificación (DP) --------------------- */
/**
 * Douglas-Peucker sobre coordenadas lon/lat aproximando dist en km.
 * Usamos distancia perpendicular aproximada en km.
 */
const pointLineDistanceKm = (p, a, b) => {
  // Si a==b, distancia al punto
  const dx = b.lng - a.lng;
  const dy = b.lat - a.lat;
  if (dx === 0 && dy === 0) return haversineKm(p, a);

  // proyección en plano lat/lng (aprox), suficiente para simplificar visual
  const t = ((p.lng - a.lng) * dx + (p.lat - a.lat) * dy) / (dx * dx + dy * dy);
  const tClamped = Math.max(0, Math.min(1, t));
  const proj = { lat: a.lat + tClamped * dy, lng: a.lng + tClamped * dx };
  return haversineKm(p, proj);
};

const douglasPeucker = (pts, toleranceKm) => {
  if (pts.length <= 2) return pts;

  const first = pts[0];
  const last = pts[pts.length - 1];

  let maxDist = 0;
  let index = -1;

  for (let i = 1; i < pts.length - 1; i++) {
    const d = pointLineDistanceKm(pts[i], first, last);
    if (d > maxDist) {
      maxDist = d;
      index = i;
    }
  }

  if (maxDist > toleranceKm && index !== -1) {
    const left = douglasPeucker(pts.slice(0, index + 1), toleranceKm);
    const right = douglasPeucker(pts.slice(index), toleranceKm);
    return left.slice(0, -1).concat(right);
  }

  return [first, last];
};

const maybeSimplify = (points, cfg) => {
  const s = cfg?.simplify;
  if (!s?.enabled) return points;
  if (!Array.isArray(points) || points.length <= s.maxPoints) return points;

  // DP reduce fuerte, luego si aún excede, muestreamos
  let reduced = douglasPeucker(points, s.toleranceKm);

  if (reduced.length > s.maxPoints) {
    const step = Math.ceil(reduced.length / s.maxPoints);
    reduced = reduced.filter((_, i) => i % step === 0);
    // asegura último
    if (reduced.at(-1)?.timestamp !== points.at(-1)?.timestamp) {
      reduced.push(points.at(-1));
    }
  }

  return reduced;
};

/* ------------------ Detección robusta de paradas ------------------ */
/**
 * Acumula secuencias de puntos "casi quietos":
 * - distancia entre puntos <= stopDistKm
 * - suma de dtSec hasta >= stopMinSec => una parada
 */
const detectStops = (points, cfg) => {
  const stops = [];
  if (points.length < 2) return stops;

  let startIdx = null;
  let accSec = 0;
  let anchor = null; // punto base para medir quietud

  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const { dtSec, dKm } = speedKmhBetween(a, b);

    const quiet = dKm <= cfg.stopDistKm && dtSec > 0;

    if (quiet) {
      if (startIdx === null) {
        startIdx = i - 1;
        accSec = 0;
        anchor = a;
      }
      accSec += dtSec;
    } else {
      if (startIdx !== null && accSec >= cfg.stopMinSec) {
        const s = points[startIdx];
        const e = points[i - 1];
        stops.push({
          startIdx: startIdx + 1,
          endIdx: i,
          lat: e.lat,
          lng: e.lng,
          start: s.timestamp,
          end: e.timestamp,
          durationSec: Math.round(accSec),
        });
      }
      startIdx = null;
      accSec = 0;
      anchor = null;
    }
  }

  // cierre
  if (startIdx !== null && accSec >= cfg.stopMinSec) {
    const s = points[startIdx];
    const e = points.at(-1);
    stops.push({
      startIdx: startIdx + 1,
      endIdx: points.length,
      lat: e.lat,
      lng: e.lng,
      start: s.timestamp,
      end: e.timestamp,
      durationSec: Math.round(accSec),
    });
  }

  return stops;
};

/* ------------------ Segmentación para evitar "volar" ------------------ */
/**
 * Separa el track en tramos confiables.
 * Devuelve:
 *  - tracks: array de arrays de puntos (cada uno un tramo)
 *  - outliers: índices de puntos/segmentos que rompieron por salto
 */
const splitIntoTracks = (points, cfg) => {
  const tracks = [];
  const outliers = [];

  if (points.length === 0) return { tracks, outliers };
  let current = [points[0]];

  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];

    const { dtSec, dKm, speedKmh } = speedKmhBetween(a, b);

    const isGap = dtSec > cfg.gapSec || dKm > cfg.gapKm || speedKmh > cfg.maxKmh;

    if (isGap) {
      outliers.push({
        atIdx: i + 1,
        reason:
          dtSec > cfg.gapSec ? "gap_time" :
          dKm > cfg.gapKm ? "gap_distance" :
          "speed_outlier",
        dtSec,
        dKm,
        speedKmh,
      });

      if (current.length >= 2) tracks.push(current);
      current = [b]; // arranca nuevo tramo
    } else {
      current.push(b);
    }
  }

  if (current.length >= 2) tracks.push(current);
  return { tracks, outliers };
};

/* --------------------------- API principal --------------------------- */
/**
 * buildTripLayers:
 * - Ordena y valida puntos
 * - (Opcional) simplifica puntos para performance
 * - Segmenta en tramos confiables (evita líneas “volando”)
 * - Genera:
 *   - pointsFC (todos los puntos)
 *   - tracksFC (MultiLineString por tramo) -> recomendado para dibujar ruta real sin “volar”
 *   - segmentsFC (pares con speedKmh) -> opcional si quieres colorear por velocidad
 *   - stopsFC (paradas acumuladas)
 *   - outliersFC (puntos problemáticos)
 *   - stats (distancia/velocidad/tiempo + calidad de data)
 */
export const buildTripLayers = (rawPoints, userCfg = {}) => {
  const cfg = { ...DEFAULT_GPS_ANALYSIS_CFG, ...userCfg, simplify: { ...DEFAULT_GPS_ANALYSIS_CFG.simplify, ...(userCfg.simplify || {}) } };

  if (!rawPoints || rawPoints.length === 0) {
    return {
      pointsFC: null,
      tracksFC: null,
      segmentsFC: null,
      stopsFC: null,
      outliersFC: null,
      stops: [],
      outliers: [],
      stats: null,
      meta: { usedPoints: 0, simplified: false },
    };
  }

  // 1) Normalizar/filtrar puntos inválidos
  const cleaned = rawPoints
    .map((p) => ({
      lat: safeNumber(p.lat),
      lng: safeNumber(p.lng),
      timestamp: p.timestamp,
    }))
    .filter((p) => p.lat !== null && p.lng !== null && p.timestamp);

  if (cleaned.length === 0) {
    return {
      pointsFC: null,
      tracksFC: null,
      segmentsFC: null,
      stopsFC: null,
      outliersFC: null,
      stops: [],
      outliers: [],
      stats: null,
      meta: { usedPoints: 0, simplified: false },
    };
  }

  // 2) Ordenar
  const ordered = [...cleaned].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  // 3) Simplificar si es enorme (solo para dibujar; stats las hacemos sobre "ordered" para precisión)
  const simplified = maybeSimplify(ordered, cfg);
  const didSimplify = simplified.length !== ordered.length;

  // 4) Points FC (para UI/popup: usa simplified para no saturar el mapa)
  const pointsFC = {
    type: "FeatureCollection",
    features: simplified.map((p, i) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [p.lng, p.lat] },
      properties: {
        idx: i + 1,
        timestamp: p.timestamp,
        timeLabel: toTZLabel(p.timestamp, cfg.timeZone),
      },
    })),
  };

  // 5) Segmentar tramos confiables (en base al track completo ordered o simplified?)
  // Para "no volar", segmentamos usando ordered (más real), pero dibujamos usando simplified segmentado:
  // Solución simple: segmentar sobre simplified (consistente visualmente).
  const { tracks, outliers } = splitIntoTracks(simplified, cfg);

  // 6) tracksFC (MultiLineString) -> recomendado para dibujar ruta real sin saltos
  const tracksFC = {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "MultiLineString",
          coordinates: tracks.map((t) => t.map((p) => [p.lng, p.lat])),
        },
        properties: {
          tracksCount: tracks.length,
          simplified: didSimplify,
        },
      },
    ],
  };

  // 7) segmentsFC por pares con velocidad (solo dentro de tramos) para color por speed
  // (opcional para tu capa real-segments)
  const segments = [];
  let distKmTotal = 0;
  let maxSpeed = 0;
  let sumSpeed = 0;
  let speedCount = 0;

  // Stats las calculamos sobre ordered (precisión) pero ignorando saltos absurdos
  const { tracks: orderedTracks } = splitIntoTracks(ordered, cfg);
  for (const t of orderedTracks) {
    for (let i = 1; i < t.length; i++) {
      const a = t[i - 1];
      const b = t[i];
      const { dtSec, dKm, speedKmh } = speedKmhBetween(a, b);
      distKmTotal += dKm;
      if (dtSec > 0) {
        maxSpeed = Math.max(maxSpeed, speedKmh);
        sumSpeed += speedKmh;
        speedCount++;
      }
    }
  }

  // segments visuales sobre simplified (para no generar miles de features)
  for (const t of tracks) {
    for (let i = 1; i < t.length; i++) {
      const a = t[i - 1];
      const b = t[i];
      const { dtSec, dKm, speedKmh } = speedKmhBetween(a, b);
      segments.push({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: [
            [a.lng, a.lat],
            [b.lng, b.lat],
          ],
        },
        properties: {
          speedKmh,
          speedBin: getSpeedBin(speedKmh, cfg.speedBins),
          startTime: a.timestamp,
          endTime: b.timestamp,
          startLabel: toTZLabel(a.timestamp, cfg.timeZone),
          endLabel: toTZLabel(b.timestamp, cfg.timeZone),
          dtSec,
          dKm,
        },
      });
    }
  }
  const segmentsFC = { type: "FeatureCollection", features: segments };

  // 8) Stops robustos (sobre ordered para precisión temporal)
  const stops = detectStops(ordered, cfg);
  const stopsFC = {
    type: "FeatureCollection",
    features: stops.map((s, i) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [s.lng, s.lat] },
      properties: {
        stopNo: i + 1,
        durationSec: s.durationSec,
        start: s.start,
        end: s.end,
        startLabel: toTZLabel(s.start, cfg.timeZone),
        endLabel: toTZLabel(s.end, cfg.timeZone),
        startIdx: s.startIdx,
        endIdx: s.endIdx,
      },
    })),
  };

  // 9) Outliers FC (para debug o toggle "ver saltos")
  const outliersFC = {
    type: "FeatureCollection",
    features: outliers.map((o) => {
      // el "atIdx" es index lógico (1-based) del punto “b” en simplified
      const p = simplified[Math.max(0, Math.min(simplified.length - 1, o.atIdx - 1))];
      return {
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.lng, p.lat] },
        properties: {
          reason: o.reason,
          dtSec: Math.round(o.dtSec),
          dKm: Number(o.dKm.toFixed(3)),
          speedKmh: Number(o.speedKmh.toFixed(1)),
        },
      };
    }),
  };

  // 10) Stats generales
  const durationSec =
    ordered.length >= 2
      ? (new Date(ordered.at(-1).timestamp) - new Date(ordered[0].timestamp)) / 1000
      : 0;

  const stats = {
    distKm: distKmTotal,
    durationSec,
    avgSpeedKmh: speedCount ? sumSpeed / speedCount : 0,
    maxSpeedKmh: maxSpeed,
    totalPoints: ordered.length,
    usedPoints: simplified.length,
    stopsCount: stops.length,
    tracksCount: orderedTracks.length,
    outliersCount: outliers.length,
  };

  return {
    pointsFC,
    // ✅ RECOMENDADO: dibujar tracksFC si NO quieres color por velocidad
    tracksFC,
    // ✅ OPCIONAL: dibujar segmentsFC si quieres color por velocidad (sin “volar” gracias al split)
    segmentsFC,
    stopsFC,
    outliersFC,
    stops,
    outliers,
    stats,
    meta: { usedPoints: simplified.length, simplified: didSimplify, timeZone: cfg.timeZone },
  };
};
